import Service, { APIVersion } from 'additiv-services';
import { getQueryStringByObject } from 'additiv-services/utils/queryUtils';
import { ServiceTypes } from 'additiv-services/types/main';
import Security from 'additiv-services/services/core/security';
import { getCustomServiceLayerHeader } from 'additiv-services/setup';
import {
    CUSTOM_SERVICE_LAYER_SERVICES,
    getCustomServiceLayerBaseUrl,
} from '../constants';

class CustomClientServices extends Service {
    static #baseServiceUrl = getCustomServiceLayerBaseUrl(
        CUSTOM_SERVICE_LAYER_SERVICES.CONTACT,
    );

    static #baseServiceUrlV11 = getCustomServiceLayerBaseUrl(
        CUSTOM_SERVICE_LAYER_SERVICES.CONTACT,
        APIVersion.V11,
    );

    static resendActivationLetter({ contactId }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${contactId}/resend-activation-letter`,
            {},
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static resendActivationLetterEnabled({ contactId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/acctivationLetter/${contactId}/canSend`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static createNewContact(payload) {
        return Service.postRequest(this.#baseServiceUrl, payload, {
            isSecure: true,
            type: ServiceTypes.WITH_MEMBER_TOKEN,
            axiosSetup: { headers: getCustomServiceLayerHeader() },
        });
    }

    static getContactDetails({ contactExternalId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/external-contact/${contactExternalId}/details`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static postTransactions(contactId, portfolioId, payload) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${contactId}/portfolios/${portfolioId}/transactions`,
            payload,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static saveClientDetails({ data, contactId }) {
        return Service.patchRequest(
            `${this.#baseServiceUrl}/${contactId}/information`,
            data,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static checkIfEmailIsInUse({ contactId, email }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${contactId}/checkemail?email=${email}`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static checkIfEmailIsAvailable({ email }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/checkemail?email=${email}`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static search({ filter, pageNumber, pageSize = 10 }) {
        const queryString = getQueryStringByObject({
            filter,
            pageNumber,
            pageSize,
        });

        return Service.getRequest(
            `${this.#baseServiceUrl}/search?${queryString}`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static patchMailingAddress({ contactId, data, action = 0 }) {
        return Service.patchRequest(
            `${this.#baseServiceUrl}/${contactId}/mailing?action=${action}`,
            data,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getConnectionsHistory({ contactId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${contactId}/connectionsHistory`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getConnections({ contactId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${contactId}/connections`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getProofOfAssetsDocs({ contactGroupId, containerId, language }) {
        const queryString = getQueryStringByObject({ language });

        return Service.getRequest(
            `${this.#baseServiceUrlV11}/contact-groups/${contactGroupId}/container/${containerId}/kyc-proofOfAssets?${queryString}`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static getContactGroups({
        contactId,
        createThreeB = false,
        createMortgage = false,
    }) {
        const queryString = getQueryStringByObject({
            createThreeB,
            createMortgage,
        });

        return Service.getRequest(
            `${this.#baseServiceUrl}/${contactId}/contactgroups?${queryString}`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getActiveApplicationIndicator({ contactGroupId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${contactGroupId}/active-application`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static async deleteProxyConnection({
        contactGroupId,
        proxyId,
        accessPolicy,
    }) {
        await Security.generateApplicationTokensIfNeeded(
            ServiceTypes.WITH_MEMBER_TOKEN,
            true,
        );

        return Service.deleteRequest(
            `${this.#baseServiceUrl}/${contactGroupId}/proxy/${proxyId}/${accessPolicy}`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getProxyAdditionStatus(contactGroupId) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${contactGroupId}/proxy/canAdd`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static deleteJAConnection({ contactGroupId }) {
        return Service.deleteRequest(
            `${this.#baseServiceUrl}/${contactGroupId}/jointaccount`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getContactGroupDetails({ groupId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${groupId}/contactgroupdetails`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static async createProxyConnection({ contactId, ...payload }) {
        await Security.generateApplicationTokensIfNeeded(
            ServiceTypes.WITH_MEMBER_TOKEN,
            true,
        );

        return Service.postRequest(
            `${this.#baseServiceUrl}/${contactId}/contactproxy`,
            payload,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static updateContactGroupProxy({ contactGroupId, ...payload }) {
        return Service.putRequest(
            `${this.#baseServiceUrl}/${contactGroupId}/contactgroupproxy`,
            payload,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static updateContactGroupAddress({ contactGroupId, ...payload }) {
        return Service.putRequest(
            `${this.#baseServiceUrl}/${contactGroupId}/contactgroupaddress`,
            payload,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static deleteContactGroup({ contactGroupId }) {
        return Service.deleteRequest(
            `${this.#baseServiceUrl}/${contactGroupId}`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static createJAConnection({ contactId, ...payload }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${contactId}/jointaccount`,
            payload,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getContactGroupAddresses({ contacts }) {
        const queryString = contacts
            .map((contact) => `contacts=${contact}`)
            .join('&');

        return Service.getRequest(
            `${this.#baseServiceUrl}/addresses?${queryString}`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getContactGroupHistory(contactGroupId) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${contactGroupId}/contactGroupHistory`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static postKYCData(contactId, payload) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${contactId}/KYC`,
            payload,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static putKYCData(contactId, payload) {
        return Service.putRequest(
            `${this.#baseServiceUrl}/${contactId}/KYC`,
            payload,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getKYCData(contactId) {
        return Service.getRequest(`${this.#baseServiceUrl}/${contactId}/KYC`, {
            isSecure: true,
            type: ServiceTypes.WITH_MEMBER_TOKEN,
            axiosSetup: { headers: getCustomServiceLayerHeader() },
        });
    }

    static isParentalAuthorityAvailable(contactId) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${contactId}/is-parental-authority`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static hasThreeBActivePortfolio({ contactId, contactGroupId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${contactId}/contact-groups/${contactGroupId}/has-active-portfolio`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getExternalInvestorProfileDataFromZKP({ contactId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${contactId}/investor-profile/external`,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static postExternalInvestorProfileToZKP({ contactId }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${contactId}/investor-profile/external`,
            null,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static postExternalKyc({ contactId }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${contactId}/kyc/external`,
            null,
            {
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }
}

export default CustomClientServices;
